import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  PaperPlaneTilt,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  FilePdf,
  ArrowsClockwise,
  Trash,
  SpinnerGap,
  EnvelopeSimple,
  CaretDown,
  MagnifyingGlass,
  User,
  CurrencyDollar,
  CalendarBlank,
  FunnelSimple
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import axiosInstance from '../api/api';

const QuotesDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, viewed: 0, accepted: 0, declined: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchQuotes();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [statusFilter]);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await axiosInstance.get('/quotes', { params });
      
      if (response.data.success) {
        setQuotes(response.data.quotes);
        setStats(response.data.stats);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load quotes',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (quoteId, quoteNumber) => {
    try {
      setActionLoading(quoteId);
      const response = await axiosInstance.get(`/quotes/${quoteId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quote_${quoteNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({ title: 'PDF Downloaded' });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to download PDF',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleResend = async (quoteId) => {
    try {
      setActionLoading(quoteId);
      const response = await axiosInstance.post(`/quotes/${quoteId}/resend`);
      
      if (response.data.success) {
        toast({ title: 'Quote Resent', description: response.data.message });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to resend quote',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateStatus = async (quoteId, newStatus) => {
    try {
      setActionLoading(quoteId);
      await axiosInstance.put(`/quotes/${quoteId}/status`, { status: newStatus });
      
      toast({ title: 'Status Updated', description: `Quote marked as ${newStatus}` });
      fetchQuotes();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    
    try {
      setActionLoading(quoteId);
      await axiosInstance.delete(`/quotes/${quoteId}`);
      
      toast({ title: 'Quote Deleted' });
      fetchQuotes();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete quote',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      sent: 'bg-blue-100 text-blue-700 border-blue-300',
      viewed: 'bg-amber-100 text-amber-700 border-amber-300',
      accepted: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      declined: 'bg-red-100 text-red-700 border-red-300'
    };
    
    const icons = {
      sent: <EnvelopeSimple size={14} weight="fill" />,
      viewed: <Eye size={14} weight="fill" />,
      accepted: <CheckCircle size={14} weight="fill" />,
      declined: <XCircle size={14} weight="fill" />
    };
    
    return (
      <Badge className={`${styles[status]} border flex items-center gap-1`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const isExpired = (validUntil) => {
    return new Date(validUntil) < new Date();
  };

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 pb-24" data-testid="quotes-dashboard">
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-[#2B4C6F] via-[#203A55] to-[#3A6A8A] text-white py-10 border-b-4 border-black">
        <div className="max-w-6xl mx-auto px-4">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-blue-100 hover:text-white mb-4 font-bold"
          >
            <ArrowLeft size={20} weight="bold" />
            Back
          </button>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center border-2 border-white/30">
              <PaperPlaneTilt size={28} weight="fill" className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black">Customer Quotes</h1>
              <p className="text-blue-100">Track and manage your sent quotes</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-4 shadow-[4px_4px_0px_#000]">
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Total</p>
            <p className="text-2xl font-black text-neutral-900 dark:text-white">{stats.total}</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-4 shadow-[4px_4px_0px_#000]">
            <p className="text-sm text-blue-500">Sent</p>
            <p className="text-2xl font-black text-blue-600">{stats.sent}</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-4 shadow-[4px_4px_0px_#000]">
            <p className="text-sm text-amber-500">Viewed</p>
            <p className="text-2xl font-black text-amber-600">{stats.viewed}</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-4 shadow-[4px_4px_0px_#000]">
            <p className="text-sm text-emerald-500">Accepted</p>
            <p className="text-2xl font-black text-emerald-600">{stats.accepted}</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-4 shadow-[4px_4px_0px_#000]">
            <p className="text-sm text-red-500">Declined</p>
            <p className="text-2xl font-black text-red-600">{stats.declined}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400">
            <FunnelSimple size={18} weight="bold" />
            <span className="font-medium">Filter:</span>
          </div>
          {['all', 'sent', 'viewed', 'accepted', 'declined'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg font-medium text-sm border-2 transition-all ${
                statusFilter === status
                  ? 'bg-[#2B4C6F] text-white border-black shadow-[2px_2px_0px_#000]'
                  : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 border-neutral-300 dark:border-neutral-600 hover:border-[#2B4C6F]'
              }`}
            >
              {status === 'all' ? 'All Quotes' : status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {/* Quotes List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <SpinnerGap size={40} className="animate-spin text-[#2B4C6F]" />
          </div>
        ) : quotes.length === 0 ? (
          <div className="bg-white dark:bg-neutral-800 border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-xl p-12 text-center">
            <PaperPlaneTilt size={64} weight="light" className="text-neutral-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">
              No Quotes Yet
            </h3>
            <p className="text-neutral-500 mb-6 max-w-md mx-auto">
              Generate an AI estimate and send it to your customers to start tracking quotes here.
            </p>
            <Button
              onClick={() => navigate('/ai-pricing')}
              className="h-12 px-6 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold rounded-xl border-2 border-black shadow-[4px_4px_0px_#000]"
            >
              Create AI Estimate
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {quotes.map((quote) => (
              <div
                key={quote.id}
                className={`bg-white dark:bg-neutral-800 border-2 border-black rounded-xl shadow-[4px_4px_0px_#000] overflow-hidden ${
                  isExpired(quote.valid_until) && quote.status === 'sent' ? 'opacity-75' : ''
                }`}
                data-testid={`quote-card-${quote.quote_number}`}
              >
                <div className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Left: Quote Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-black text-lg text-[#2B4C6F]">
                          {quote.quote_number}
                        </span>
                        {getStatusBadge(quote.status)}
                        {isExpired(quote.valid_until) && quote.status === 'sent' && (
                          <Badge className="bg-neutral-100 text-neutral-600 border border-neutral-300">
                            Expired
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-600 dark:text-neutral-400">
                        <span className="flex items-center gap-1">
                          <User size={14} weight="bold" />
                          {quote.customer.name}
                        </span>
                        <span className="flex items-center gap-1">
                          <EnvelopeSimple size={14} />
                          {quote.customer.email}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-sm">
                        <span className="text-neutral-900 dark:text-white font-medium">
                          {quote.job_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                        {quote.location && (
                          <span className="text-neutral-500">• {quote.location}</span>
                        )}
                      </div>
                    </div>

                    {/* Right: Price & Date */}
                    <div className="text-right">
                      <div className="flex items-center gap-1 justify-end mb-1">
                        <CurrencyDollar size={18} weight="bold" className="text-emerald-500" />
                        <span className="font-black text-lg text-neutral-900 dark:text-white">
                          ${quote.price_low.toLocaleString()} - ${quote.price_high.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 justify-end text-sm text-neutral-500">
                        <CalendarBlank size={14} />
                        Sent {new Date(quote.created_at).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-neutral-400 mt-1">
                        Valid until {new Date(quote.valid_until).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadPdf(quote.id, quote.quote_number)}
                      disabled={actionLoading === quote.id}
                      className="h-9 px-3 text-sm font-medium rounded-lg border-2"
                    >
                      <FilePdf size={16} className="mr-1 text-red-500" />
                      PDF
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleResend(quote.id)}
                      disabled={actionLoading === quote.id}
                      className="h-9 px-3 text-sm font-medium rounded-lg border-2"
                    >
                      <ArrowsClockwise size={16} className="mr-1" />
                      Resend
                    </Button>

                    {quote.status === 'sent' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateStatus(quote.id, 'viewed')}
                          disabled={actionLoading === quote.id}
                          className="h-9 px-3 text-sm font-medium rounded-lg border-2 text-amber-600 border-amber-300 hover:bg-amber-50"
                        >
                          <Eye size={16} className="mr-1" />
                          Mark Viewed
                        </Button>
                      </>
                    )}

                    {(quote.status === 'sent' || quote.status === 'viewed') && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleUpdateStatus(quote.id, 'accepted')}
                          disabled={actionLoading === quote.id}
                          className="h-9 px-3 text-sm font-medium rounded-lg border-2 bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-600"
                        >
                          <CheckCircle size={16} className="mr-1" />
                          Accepted
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateStatus(quote.id, 'declined')}
                          disabled={actionLoading === quote.id}
                          className="h-9 px-3 text-sm font-medium rounded-lg border-2 text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <XCircle size={16} className="mr-1" />
                          Declined
                        </Button>
                      </>
                    )}

                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(quote.id)}
                      disabled={actionLoading === quote.id}
                      className="h-9 px-3 text-sm font-medium rounded-lg text-neutral-500 hover:text-red-500 hover:bg-red-50 ml-auto"
                    >
                      <Trash size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuotesDashboard;
